import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/features/auth/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';

export interface Appointment {
  id: string;
  owner_id: string;
  client_id: string | null;
  client_name: string;
  project_name: string;
  date: string;
  start_time: string;
  duration_minutes: number;
  price: number;
  is_paid: boolean;
  status: string;
  notes: string;
  created_at: string;
  updated_at: string;
}

export function useAppointments() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['appointments', user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('appointments')
        .select('*')
        .eq('owner_id', user!.id)
        .order('date', { ascending: true })
        .order('start_time', { ascending: true });
      if (error) throw error;
      return (data || []) as unknown as Appointment[];
    },
    enabled: !!user,
  });
}

export function useTodayAppointments() {
  const { data: all = [] } = useAppointments();
  const today = new Date().toISOString().split('T')[0];
  return all.filter(a => a.date === today);
}

export function useUpcomingAppointments() {
  const { data: all = [] } = useAppointments();
  const today = new Date().toISOString().split('T')[0];
  return all.filter(a => a.date >= today && a.status !== 'cancelled').slice(0, 10);
}

export function useSaveAppointment() {
  const { user } = useAuth();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (appt: Partial<Appointment> & { client_name: string; date: string; start_time: string }) => {
      if (appt.id) {
        const { error } = await supabase.from('appointments').update({
          client_name: appt.client_name,
          project_name: appt.project_name || '',
          date: appt.date,
          start_time: appt.start_time,
          duration_minutes: appt.duration_minutes || 60,
          price: appt.price || 0,
          is_paid: appt.is_paid || false,
          status: appt.status || 'scheduled',
          notes: appt.notes || '',
          client_id: appt.client_id || null,
        }).eq('id', appt.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from('appointments').insert({
          owner_id: user!.id,
          client_name: appt.client_name,
          project_name: appt.project_name || '',
          date: appt.date,
          start_time: appt.start_time,
          duration_minutes: appt.duration_minutes || 60,
          price: appt.price || 0,
          is_paid: appt.is_paid || false,
          status: appt.status || 'scheduled',
          notes: appt.notes || '',
          client_id: appt.client_id || null,
        });
        if (error) throw error;
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['appointments'] }),
  });
}

export function useDeleteAppointment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('appointments').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['appointments'] }),
  });
}
